import type { ReactElement } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { cn } from '@core/utils/cn';

/**
 * Tooltip that can be switched off.
 *
 * Radix Tooltip has no `disabled` prop, but the sidebar needs
 * `[matTooltip]="!isSidebarOpen ? 'Dashboard' : ''"` — a tooltip that exists only while
 * the rail is collapsed. Returning bare children when `when` is false is both correct
 * and cheaper than mounting a Root that never opens.
 */
export function ConditionalTooltip({
  when,
  content,
  side = 'top',
  children,
}: {
  when: boolean;
  content: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  children: ReactElement;
}) {
  if (!when || !content) return children;

  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          sideOffset={6}
          className={cn(
            'z-[1200] max-w-[240px] rounded-xs bg-gray-800 px-2 py-1 text-2xs text-white shadow-pop',
            'data-[state=delayed-open]:animate-[vm-pop-in_120ms_ease-out]',
          )}
        >
          {content}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}

/** Always-on tooltip, for the many `matTooltip="..."` usages across the tables. */
export function Tooltip({
  content,
  side = 'top',
  children,
}: {
  content: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  children: ReactElement;
}) {
  return (
    <ConditionalTooltip when={!!content} content={content} side={side}>
      {children}
    </ConditionalTooltip>
  );
}
